import { useEffect, useState } from "react";
import Modal from "../../../../components/common/Modal";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import TextInput, { InputType } from "../../../../components/forms/textInput";

import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../../../../firebase/firebase";
import { RemoteConfig } from "../../../../ampli/constants";
import {
  isValidSaudiPhoneNumber,
  normalizeSaudiNumber,
} from "../../../../setup/util/phoneNumber.helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import { ESenderRole } from "../../../../setup/api/services/contract/models";

interface IAddPartyModalProps {
  handleClose: () => void;
  handleSave: (partyData: Inputs) => void;
  modalOpen: boolean;
  partyIndex: number;
  type: "customer" | "provider";
  mode: string;
}

export type Inputs = {
  id: string;
  rowId?: string; // Add rowId for error mapping
  name: string;
  phoneNumber: string;
  amount: number | null;
  fee?: number;
  type: string;
  role?: string;
};

const AddPartyModal = ({
  handleClose,
  modalOpen,
  handleSave,
  type,
  partyIndex,
  mode,
}: IAddPartyModalProps) => {
  const [maxPrice, setMaxPrice] = useState(1000000);
  const { t } = useTranslation();
  const clientAdminPhoneNumber = AuthConfig.getUserInfo()?.phoneNumber;

  const isDuplicate = (phoneNumber) => {
    const currentMilestoneIndex = watch("currentMilestoneIndex");
    const allParties = watch(`milestones.${currentMilestoneIndex}.parties`);

    return allParties?.some(
      (p, i) => p.phoneNumber === phoneNumber && partyIndex !== i
    );
  };

  const schema = z
    .object({
      id: z.string(),
      name: z
        .string()
        .min(5, { message: t("err_min_chars", { min: 5 }) })
        .max(100, { message: t("err_max_chars", { max: 100 }) })
        .trim()
        .nonempty({ message: t("err_required") }),
      phoneNumber: z
        .string()
        .refine((val) => isValidSaudiPhoneNumber(val), {
          message: t("err_invalid_phone"),
        })
        .refine((val) => !isDuplicate(val), {
          message: t("err_duplicate_phone"),
        }),
      amount: z
        .number()
        .nullable()
        .refine((val) => val !== null, {
          message: t("err_required"),
        }),
      fee: z.number().optional(),
      type: z.string().nonempty({ message: t("err_required") }),
      role: z.string().optional(),
    })
    .refine((data) => data.type !== "provider" || !!data.role, {
      path: ["role"],
      message: t("err_role_required_for_providers"),
    })
    .superRefine((data, ctx) => {
      if (
        (data.phoneNumber !== clientAdminPhoneNumber &&
          data?.amount !== null &&
          data?.amount < 1) ||
        (data?.amount !== null && data?.amount < 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amount"],
          message: t("err_number_min", {
            min: data.phoneNumber !== clientAdminPhoneNumber ? 1 : 0,
          }),
        });
      }
    });

  const { watch } = useFormContext();
  const {
    control: partyControl,
    reset,
    watch: partyWatch,
    setValue: setPartyValue,
    formState: { isValid, errors, isDirty },
  } = useForm<Inputs>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      id: "",
      name: "",
      phoneNumber: "",
      amount: undefined,
      fee: 0,
      role: "",
      type: "customer",
    },
  });
  const phoneNumberFieldValue = useWatch({
    control: partyControl,
    name: "phoneNumber",
  });

  const currentMilestoneIndex = watch("currentMilestoneIndex") || 0;
  const parties = watch(`milestones.${currentMilestoneIndex}.parties`);

  useEffect(() => {
    if (parties?.[partyIndex]?.id && mode === "edit") {
      reset({ ...parties?.[partyIndex] });
    } else {
      reset({
        id: `${type}-${partyIndex}`,
        rowId: `${type}-${partyIndex}-${Date.now()}`, // Add unique rowId
        name: "",
        phoneNumber: "",
        amount: null,
        fee: 0,
        type,
        role: "",
      });
    }
  }, [partyIndex, reset, parties, mode, type]);

  useEffect(() => {
    const fetchFirebaseConfig = async () => {
      await fetchAndActivate(remoteConfig);

      const maxPriceValue = getValue(
        remoteConfig,
        "max_product_price"
      ) as unknown as RemoteConfig;

      setMaxPrice(+maxPriceValue?._value);
    };
    fetchFirebaseConfig();
  }, []);

  useEffect(() => {
    setPartyValue("fee", 0);
  }, [setPartyValue]);

  function normalizePastedNumber(pasted: string): string {
    const digits = pasted.replace(/[^\d]/g, "");
    if (digits.length <= 1) return digits;

    const reversed = digits.split("").reverse().join("");

    // If the original came from RTL context (Arabic digits, etc.), reverse it
    const hasArabic = /[\u0600-\u06FF\u0660-\u0669]/.test(pasted);
    return hasArabic ? reversed : digits;
  }

  return (
    <Modal
      customModalContainerClassName={styles.modalContainer}
      customModalClassName={styles.modal}
      visible={modalOpen}
      onCancel={() => {
        handleClose();
      }}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {type === "customer" ? t("lbl_add_customer") : t("lbl_add_provider")}
        </h1>
        <Controller
          name={`name`}
          control={partyControl}
          rules={{ required: true, max: 100, min: 5 }}
          render={({ field }) => (
            <TextInput
              {...field}
              maxCharacters={100}
              required
              disabled={clientAdminPhoneNumber === phoneNumberFieldValue}
              placeholder={t("lbl_name")}
              errorMessage={errors?.name?.message?.toString()}
            />
          )}
        />
        <Controller
          name={`phoneNumber`}
          control={partyControl}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              {...field}
              maxCharacters={100}
              required
              onChange={(e) => {
                const value = e?.target?.value.replace(/\s+/g, ""); // remove spaces

                field?.onChange(normalizeSaudiNumber(value));
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text");
                field.onChange(
                  normalizeSaudiNumber(normalizePastedNumber(pasted))
                );
              }}
              type={InputType.PHONE_NUMBER}
              disabled={clientAdminPhoneNumber === phoneNumberFieldValue}
              placeholder={t("lbl_mobile_number")}
              errorMessage={errors?.phoneNumber?.message?.toString()}
            />
          )}
        />
        {type === "provider" && (
          <Controller
            name={`role`}
            control={partyControl}
            rules={{ required: true }}
            render={({ field }) => (
              <TextInput
                {...field}
                maxCharacters={100}
                required
                value={
                  field?.value === ESenderRole.BROKER
                    ? t("lbl_arbitrator_title")
                    : field?.value
                }
                disabled={clientAdminPhoneNumber === phoneNumberFieldValue}
                placeholder={t("lbl_role")}
                errorMessage={errors?.role?.message?.toString()}
              />
            )}
          />
        )}
        <Controller
          name={`amount`}
          control={partyControl}
          rules={{
            required: true,
            max: maxPrice,
            min: type === "customer" ? 1 : 0,
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field?.value !== null ? field?.value : ""}
              onChange={(event) => {
                field?.onChange(
                  event?.target?.value === "" ? null : +event?.target?.value
                );
              }}
              maxCharacters={100}
              required
              description={t("lbl_saudi_currency")}
              type={InputType.NUMBER}
              placeholder={t("lbl_value")}
              errorMessage={errors?.amount?.message?.toString()}
            />
          )}
        />

        <PrimaryButton
          disabled={mode === "edit" ? !isDirty || !isValid : !isValid}
          style={{
            marginTop: "8px",
            background: (mode === "edit" ? isValid && isDirty : isValid)
              ? "#0D44AD"
              : "#EEF0F5",
          }}
          onClick={handleSave?.bind(null, partyWatch())}>
          {mode === "add" ? t("lbl_add") : t("lbl_edit")}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default AddPartyModal;
